import React, { useEffect, useState } from "react";
import useSkillset from "../../customHooks/useSkillset";
import ReasonModal from "../../modals/ReasonModal";
import ViewReasonModal from "../../modals/ViewReasonModal";
import ResultsWrapper from "../../modals/ResultsWrapper";
import UserPreview from "../../modals/UserPreview";
import ReadApplicationLetter from "../../modals/ReadApplicationLetter";
import { toast, Bounce } from "react-toastify";
import CustomToastMessage from "../../common/CustomToastMessage";
import NameLink from "../NameLink";
import useScreenSize from "../../customHooks/useScreenSize";
import RequestTeamLimitChange from "../../modals/RequestTeamLimitChange";
import { makeStyles } from'@mui/styles';
import moment from "moment";
import { Avatar, Typography, Grid } from "@mui/material";

const useStyles = makeStyles((theme) => ({
  avatarDiv: {
    display: "contents",
  },
  avatar: {
    marginTop: 15,
    backgroundColor: "#F8C22B",
    height: 80,
    width: 80,
  },
  created: {
    color: "rgba(0, 27, 17, 0.6)",
    fontFamily: "Open Sans",
    fontWeight: 400,
    fontStyle: "italic",
    paddingBottom: 5,
    paddingTop: 5,
    fontSize: 13,
  },
}));

const Application = ({
  appData,
  appType,
  fetchApplicationData,
  teamData,
  fetchTeamData,
}) => {
  const [applicantsInfo, setApplicantsInfo] = useState({});
  const [showDeclineBox, toggleDeclineBox] = useState(false);
  const [showViewReasonModal, toggleReason] = useState(false);
  const [showUserPreview, toggleUserPreview] = useState(false);
  const [showLetter, setShowLetter] = useState(false);
  const [showRequestModal, setShowResquestModal] = useState(false);
  const [limitType, setLimitType] = useState("team members");
  const [headerColor, setHeaderColor] = useState("");

  const classes = useStyles();

  const toggleApplicationLetter = () => {
    setShowLetter(!showLetter);
  };

  const toggleRequestModal = () => {
    setShowResquestModal(!showRequestModal);
  };

  // Toast message function
  const notify = (message) =>
    toast.info(<CustomToastMessage message={message} />, {
      autoClose: 2000,
      closeButton: true,
      hideProgressBar: false,
      transition: Bounce,
      position: "bottom-center",
    });

  const acceptApplication = async () => {
    try {
      const payload = {
        publicApplicateId: appData.applicant_publicId,
        teamId: teamData.public_team_id,
        appId: appData.id,
      };

      // Add user to team
      const res = await api.
post(
        "/api/v1/invites/team/applications/accept",
        payload,
      );

      if (res.data.msg === "max members") {
        // Toggle modal
        toggleRequestModal();
        setLimitType("team members");
      } else {
        // Refresh application data
        fetchApplicationData();
        // Toast message
        notify(res.data.msg);
        // Update team data
        fetchTeamData();
      }
    } catch (err) {
      throw new Error(err);
    }
  };

  const declineApplication = () => {
    toggleDeclineBox(!showDeclineBox);
  };

  const deleteApplication = async () => {
    try {
      await api.
delete(`api/v1/invites/app/delete/${appData.id}`);
      // Refresh application data
      fetchApplicationData();
    } catch (err) {
      throw new Error(err);
    }
  };

  const toggleReasonModal = () => {
    toggleReason(!showViewReasonModal);
  };

  const showUserPreviewModal = () => {
    toggleUserPreview(!showUserPreview);
  };

  useEffect(() => {
    // Fetch applicants profile
    const fetchApplicantsProfile = async () => {
      const res = await api.
get(
        `/api/v1/profiles/profile/${appData.applicant_publicId}`,
      );

      if (res.status === 200) {
        // Set state
        setApplicantsInfo(res.data);
      } else {
        throw new Error("Error fetching applicant's profile");
      }
    };

    // On mount
    if (appData.applicant_publicId !== undefined) {
      fetchApplicantsProfile();
    }
  }, [appData.applicant_publicId]);

  useEffect(() => {
    if (appData.response === "Waiting on review") {
      setHeaderColor("orange");
    } else {
      setHeaderColor("red");
    }
  }, [appData]);

  const skillsetText = useSkillset(applicantsInfo.Skills);

  // Custom hook for screen size.
  const screenLayoutType = useScreenSize();

  if (appType === "received") {
    return (
      <>
        {showRequestModal && (
          <RequestTeamLimitChange
            toggleModal={toggleRequestModal}
            limitName={limitType}
            publicTeamId={teamData.public_team_id}
          />
        )}
        {showDeclineBox && (
          <ReasonModal
            toggleMethod={toggleDeclineBox}
            questionText="Decline Application"
            appId={appData.id}
            fetchApplicationData={fetchApplicationData}
          />
        )}
        {showUserPreview && (
          <ResultsWrapper togglePreview={showUserPreviewModal}>
            <UserPreview userInfo={applicantsInfo} />
          </ResultsWrapper>
        )}
        {showLetter && (
          <ResultsWrapper togglePreview={toggleApplicationLetter}>
            <ReadApplicationLetter
              name={applicantsInfo.fullname}
              letterText={appData.application_letter}
            />
          </ResultsWrapper>
        )}

        <div className="container--table_row">
          <NameLink userData={applicantsInfo} aria-label="Applicant Name" />
          {screenLayoutType === "largeScreen" ||
            (screenLayoutType === "mediumScreen" && (
              <>
                <p className="text text--table_data" role="text">{skillsetText}</p>
                <button
                  className="button button--dashboard-med button--dashboard-secondary table_button"
                  onClick={toggleApplicationLetter}
                  aria-label="View Letter"
                >
                  View Letter
                </button>
              </>
            ))}
          <button
            className="button button--dashboard-small button--dashboard-primary table_button"
            onClick={acceptApplication}
            aria-label="Accept Application"
          >
            Accept
          </button>
          <button
            className="button button--dashboard-small button--dashboard-secondary table_button"
            onClick={declineApplication}
            aria-label="Decline Application"
          >
            Decline
          </button>
        </div>
      </>
    );
  }

  if (appType === "sent") {
    return (
      <div
        className={
          "container--app_sent_row " +
          (appData.response === "Declined" ? "clickable-app_sent" : null)
        }
        onClick={appData.response === "Declined" ? toggleReasonModal : null}
      >
        <div className={`card_header_${headerColor}`} role="alert">
          <p className="card_header_text">{appData.response}</p>
        </div>
        <Grid item xs={6} className={classes.avatarDiv}>
          <Avatar aria-label="team" className={classes.avatar} component="div">
            {appData.Team.title.charAt(0)}
          </Avatar>
        </Grid>
        <p className="text text--invite_data">{appData.Team.title}</p>
        <Typography variant="body2" className={classes.created} aria-label="Application Date" >
          Applied on {moment(appData.createdAt).format("MMMM Do, YYYY")}
        </Typography>
        <div className="container--app_sent_button_wrapper">
          {showViewReasonModal && (
            <ViewReasonModal
              toggleMethod={toggleReasonModal}
              response_reason={appData.response_reason}
            />
          )}
        </div>
      </div>
    );
  }
};

export default Application;
